import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, setPasswordWithPin } = useAuth();
  const nav = useNavigate();
  // If a session already exists (e.g. preview auto-login), skip the login screen.
  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);
  const [mode, setMode] = useState("login"); // login | setpwd | recover
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  // "checking" while we probe the backend, "enabled" when Entra is configured, "unconfigured"
  // only when the backend EXPLICITLY reports configured:false, "unreachable" when the backend
  // can't be reached (transient outage) — the latter auto-retries instead of dead-ending.
  const [msStatus, setMsStatus] = useState("checking");
  // Persistent reason shown as a red banner when a Microsoft sign-in bounced back (toasts vanish
  // too fast for users to read why they were denied).
  const [msError, setMsError] = useState("");
  // Break-glass: the password / PIN / admin-recovery UI is hidden unless the admin opens
  // /login?recovery=1. Microsoft 365 is the only visible sign-in method by default.
  const [recoveryMode] = useState(
    () => new URLSearchParams(window.location.search).get("recovery") === "1"
  );

  // Surface a readable reason if a Microsoft sign-in bounced back here, and clean the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("ms_error");
    if (err) {
      toast.error(err);
      setMsError(err);
      window.history.replaceState({}, document.title, "/login");
    }
  }, []);

  // Probe the backend for Entra config. Distinguishes an explicit "not configured" (admin must
  // act) from a transient "backend unreachable" (auto-retries so a server blip self-heals).
  useEffect(() => {
    let cancelled = false;
    let attempt = 0;
    let timer = null;
    const check = async () => {
      try {
        const r = await api.get("/auth/microsoft/status");
        if (cancelled) return;
        setMsStatus(r.data?.configured ? "enabled" : "unconfigured");
      } catch (err) {
        if (cancelled) return;
        // 200 with configured:false never lands here; this branch = network error / 5xx / timeout.
        setMsStatus("unreachable");
        attempt += 1;
        // Back off: 2s, 4s, 6s … capped at 10s, retry indefinitely until the server returns.
        const delay = Math.min(2000 * attempt, 10000);
        timer = setTimeout(check, delay);
      }
    };
    check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const retryStatus = () => {
    setMsStatus("checking");
    api.get("/auth/microsoft/status")
      .then((r) => setMsStatus(r.data?.configured ? "enabled" : "unconfigured"))
      .catch(() => setMsStatus("unreachable"));
  };

  const onMicrosoft = () => {
    window.location.href = `${API_BASE}/auth/microsoft/login`;
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onSetPwd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setPasswordWithPin(username, pin, newPassword);
      toast.success("Password set. You're now signed in.");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not set password");
    } finally {
      setLoading(false);
    }
  };

  const onRecover = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-reset", {
        token: resetToken.trim(),
        new_password: newPassword,
        username: username.trim() || "rishabh25467",
      });
      toast.success(data?.message || "Password reset. Sign in with your new password.");
      setMode("login");
      setPassword("");
      setResetToken("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="jde-login-bg">
      <div className="jde-login-card" data-testid="login-card">
        {recoveryMode && (
        <div className="jde-login-header">
          <div style={{ fontSize: 11, opacity: 0.85, letterSpacing: "0.14em", fontWeight: 600 }}>
            ORDER HUB
          </div>
          <div style={{ fontSize: 11, opacity: 0.55, marginTop: 2 }}>· Enterprise ·</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6, color: 'var(--omh-text)', letterSpacing: '-0.02em' }}>
            <ShieldCheck size={20} weight="bold" style={{ display: "inline", marginRight: 8, marginBottom: 3, color: 'var(--omh-primary)' }} />
            Sign in
          </div>
        </div>
        )}

        <div style={{ padding: 18 }}>
          {!recoveryMode ? (
            <div data-testid="login-microsoft-only" style={{ textAlign: "center", padding: "14px 6px 6px" }}>
              <div style={{
                width: 60, height: 60, borderRadius: 16, margin: "0 auto 18px",
                background: "#e3f1f1", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ShieldCheck size={30} weight="fill" style={{ color: "#0f7b8c" }} />
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--omh-text, #1f2a2e)", letterSpacing: "-0.02em" }}>
                Order Hub
              </div>
              <div style={{ fontSize: 14, color: "var(--jde-text-muted, #6b7a80)", margin: "8px auto 22px", maxWidth: 260, lineHeight: 1.5 }}>
                Sign in with your organization account to continue
              </div>
              {msError && (
                <div
                  data-testid="login-ms-error"
                  style={{
                    background: "#fdecec", border: "1px solid #f5c2c2", color: "#a12020",
                    borderRadius: 10, padding: "10px 12px", margin: "0 auto 18px", maxWidth: 300,
                    fontSize: 13, lineHeight: 1.45, textAlign: "left",
                  }}
                >
                  <strong style={{ display: "block", marginBottom: 2 }}>Sign-in was blocked</strong>
                  {msError}
                </div>
              )}
              {msStatus === "enabled" ? (
                <button
                  data-testid="login-microsoft"
                  type="button"
                  onClick={onMicrosoft}
                  className="jde-ms-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                  </svg>
                  Continue with Microsoft
                </button>
              ) : msStatus === "checking" ? (
                <div data-testid="login-ms-checking" style={{ fontSize: 13, color: "var(--jde-text-muted, #6b7a80)" }}>
                  Checking sign-in…
                </div>
              ) : msStatus === "unreachable" ? (
                <div data-testid="login-ms-unreachable" style={{ fontSize: 13, color: "var(--jde-text-muted, #6b7a80)" }}>
                  <div>Can’t reach the server right now — reconnecting…</div>
                  <button
                    data-testid="login-ms-retry"
                    type="button"
                    onClick={retryStatus}
                    className="jde-ms-btn"
                    style={{ marginTop: 12 }}
                  >
                    Retry now
                  </button>
                </div>
              ) : (
                <div data-testid="login-ms-unconfigured" style={{ fontSize: 13, color: "var(--omh-danger, #b00020)" }}>
                  Microsoft sign-in is temporarily unavailable. Please contact your administrator.
                </div>
              )}
              <div style={{ fontSize: 12, color: "var(--jde-text-muted, #94a3a8)", margin: "20px auto 2px", maxWidth: 280, lineHeight: 1.5 }}>
                Access is managed by your IT administrator. Contact them if you're unable to sign in.
              </div>
            </div>
          ) : (
          <>
          <div style={{ display: "flex", gap: 0, marginBottom: 14, borderBottom: "1px solid var(--jde-border-subtle)" }}>
            <button
              data-testid="tab-login"
              type="button"
              onClick={() => setMode("login")}
              className="jde-tab"
              style={{
                borderBottom: mode === "login" ? "2px solid var(--jde-primary)" : "2px solid transparent",
                color: mode === "login" ? "var(--jde-primary)" : undefined,
              }}
            >Sign In</button>
            <button
              data-testid="tab-setpwd"
              type="button"
              onClick={() => setMode("setpwd")}
              className="jde-tab"
              style={{
                borderBottom: mode === "setpwd" ? "2px solid var(--jde-primary)" : "2px solid transparent",
                color: mode === "setpwd" ? "var(--jde-primary)" : undefined,
              }}
            >First-Time Setup (PIN)</button>
            <button
              data-testid="tab-recover"
              type="button"
              onClick={() => setMode("recover")}
              className="jde-tab"
              style={{
                borderBottom: mode === "recover" ? "2px solid var(--jde-primary)" : "2px solid transparent",
                color: mode === "recover" ? "var(--jde-primary)" : undefined,
              }}
            >Admin Recovery</button>
          </div>

          {mode === "login" ? (
            <form onSubmit={onLogin}>
              <div style={{ fontSize: 11, color: "var(--jde-text-muted)", marginBottom: 10 }}>
                Emergency password sign-in. The standard way to sign in is Microsoft 365.
              </div>
              <label className="jde-label">Username</label>
              <input
                data-testid="login-username"
                className="jde-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
              <div style={{ height: 10 }} />
              <label className="jde-label">Password</label>
              <input
                data-testid="login-password"
                type="password"
                className="jde-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <div style={{ height: 14 }} />
              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="jde-btn jde-btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "6px 10px" }}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          ) : mode === "setpwd" ? (
            <form onSubmit={onSetPwd}>
              <div style={{ fontSize: 11, color: "var(--jde-text-muted)", marginBottom: 10 }}>
                Enter the one-time PIN your administrator gave you, then set your password.
              </div>
              <label className="jde-label">Username</label>
              <input
                data-testid="setpwd-username"
                className="jde-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
              />
              <div style={{ height: 10 }} />
              <label className="jde-label">One-Time PIN</label>
              <input
                data-testid="setpwd-pin"
                className="jde-input text-mono"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                maxLength={6}
              />
              <div style={{ height: 10 }} />
              <label className="jde-label">New Password (min 6 chars)</label>
              <input
                data-testid="setpwd-newpassword"
                type="password"
                className="jde-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <div style={{ height: 14 }} />
              <button
                data-testid="setpwd-submit"
                type="submit"
                disabled={loading}
                className="jde-btn jde-btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "6px 10px" }}
              >
                {loading ? "Setting…" : "Set Password & Sign In"}
              </button>
            </form>
          ) : (
            <form onSubmit={onRecover}>
              <div style={{ fontSize: 11, color: "var(--jde-text-muted)", marginBottom: 10 }}>
                Recover the admin account using your operator reset token. Set a new password,
                then sign in. (Token is configured by your administrator on the server.)
              </div>
              <label className="jde-label">Admin Username</label>
              <input
                data-testid="recover-username"
                className="jde-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="rishabh25467"
                autoFocus
              />
              <div style={{ height: 10 }} />
              <label className="jde-label">Reset Token</label>
              <input
                data-testid="recover-token"
                type="password"
                className="jde-input text-mono"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
              />
              <div style={{ height: 10 }} />
              <label className="jde-label">New Password (min 6 chars)</label>
              <input
                data-testid="recover-newpassword"
                type="password"
                className="jde-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <div style={{ height: 14 }} />
              <button
                data-testid="recover-submit"
                type="submit"
                disabled={loading}
                className="jde-btn jde-btn-primary"
                style={{ width: "100%", justifyContent: "center", padding: "6px 10px" }}
              >
                {loading ? "Resetting…" : "Reset Admin Password"}
              </button>
            </form>
          )}
          </>
          )}

          {recoveryMode && (
          <div style={{
            marginTop: 14, fontSize: 10, color: "var(--jde-text-muted)",
            borderTop: "1px solid var(--jde-border-subtle)", paddingTop: 10, textAlign: "center"
          }}>
            v1.0 • EnterpriseOne-compatible
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
